import type { FC } from "react"
import React, { useEffect, useMemo, useState } from "react"
import type { StackProps } from "@chakra-ui/react"
import {
  Stack,
  Skeleton,
  Flex,
  Icon,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
} from "@chakra-ui/react"

import type {
  ActionsSecurityCheckMetadata,
  ProposalSecurityCheckQuery,
  ProposalSecurityCheckQueryVariables,
  TransactionSimulationV2,
} from "query/graphql"
import { ProposalSecurityCheckDocument, SimulationStatus } from "query/graphql"
import CardContainer from "common/components/CardContainer"
import ChevronRight from "ui/components/icons/ChevronRight"
import ImpactOverview from "common/components/ImpactOverview"
import BrakeWarning from "ui/components/icons/BrakeWarning"
import ShieldCheck from "ui/components/icons/ShieldCheck"
import { useQuery } from "query/hooks/useQuery"
import { GrpcErrorType } from "common/helpers/fetcher"
import { FeatureFlag, useGovernanceContext } from "governance/context"
import { chainIdToChainReference } from "web3/helpers/transformers"
import { isSecurityCheckSupportedInChain } from "web3/helpers/chains"

type Props = {
  proposalId: string
  executablesLength: number
  chainId: string
}

const TallyProposalImpactOverviewSummary: FC<Props & StackProps> = ({
  proposalId,
  chainId,
  executablesLength,
  ...stackProps
}) => {
  const { isFeatureFlagOn } = useGovernanceContext()
  const hideSimulation = isFeatureFlagOn(FeatureFlag.HideSimulation)

  const isSecurityCheckSupported = isSecurityCheckSupportedInChain(
    chainIdToChainReference(chainId),
  )

  const { isLoading, data, error } = useQuery<
    ProposalSecurityCheckQuery,
    ProposalSecurityCheckQueryVariables
  >(
    {
      query: ProposalSecurityCheckDocument,
      variables: {
        proposalId,
      },
      omittedErrors: [GrpcErrorType.NotFound],
    },
    { enabled: isSecurityCheckSupported },
  )

  const [isModalOpen, setModalOpen] = useState(false)
  const [metadata, setMetadata] = useState<ActionsSecurityCheckMetadata | null>(
    null,
  )
  const [simulations, setSimulations] = useState<
    TransactionSimulationV2[] | null
  >(null)

  const [noThreatsDetected, setNoThreatsDetected] = useState(false)
  const [someSimulationFailed, setSomeSimulationFailed] = useState(false)
  const [isSimulationsAvailable, setIsSimulationsAvailable] = useState<
    boolean | undefined
  >(undefined)

  const toggleModal = () => setModalOpen(!isModalOpen)

  useEffect(() => {
    if (!isLoading && data?.proposalSecurityCheck) {
      const proposalSecurityCheckMetadata =
        data?.proposalSecurityCheck.metadata.metadata

      const proposalSecurityCheckSimulations =
        data?.proposalSecurityCheck.metadata.simulations

      setMetadata(proposalSecurityCheckMetadata)
      setSimulations(proposalSecurityCheckSimulations)

      // Determine if there are no threats detected
      const hasThreatAnalysis = !!proposalSecurityCheckMetadata?.threatAnalysis
      const threatAnalysisEvents =
        proposalSecurityCheckMetadata?.threatAnalysis?.actionsData?.flatMap(
          (actionsData) =>
            actionsData?.events?.filter((event) => event.severity !== "None") ??
            [],
        ) ?? []

      const noThreats = hasThreatAnalysis && threatAnalysisEvents.length === 0
      setNoThreatsDetected(noThreats)

      const someSimulationFailed =
        proposalSecurityCheckSimulations?.some(
          (simulation) => simulation.result === SimulationStatus.Failed,
        ) ?? false
      setSomeSimulationFailed(someSimulationFailed)

      setIsSimulationsAvailable(proposalSecurityCheckSimulations?.length > 0)
    } else {
      setMetadata(null)
      setSimulations(null)
      setNoThreatsDetected(false)
      setSomeSimulationFailed(false)
      setIsSimulationsAvailable(false)
    }
  }, [data, isLoading])

  const displayThreatAnalysis = useMemo(() => {
    const threatAnalysisEvents =
      metadata?.threatAnalysis?.actionsData?.flatMap(
        (actionsData) =>
          actionsData?.events?.filter((event) => event.severity !== "None") ??
          [],
      ) ?? null

    return (
      <Stack direction="row" justifyContent="space-between" {...stackProps}>
        <Text cursor="pointer" onClick={toggleModal}>
          {" "}
          <Icon as={BrakeWarning} fill="purple.600" /> Threat analysis
        </Text>
        <Text color="gray.700" cursor="pointer" onClick={toggleModal}>
          {!metadata?.threatAnalysis
            ? "Unavailable"
            : threatAnalysisEvents && threatAnalysisEvents?.length > 0
            ? `${threatAnalysisEvents.length} warnings`
            : "None detected"}
        </Text>
      </Stack>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata, someSimulationFailed])

  if (!isSecurityCheckSupported) {
    return null
  }

  const displaySimulations = () => {
    if (hideSimulation) return null

    const getSimulationLabel = () => {
      if (!isSimulationsAvailable) return "Unavailable"

      return someSimulationFailed ? "Failed" : "Passed"
    }

    return (
      <Stack direction="row" justifyContent="space-between" {...stackProps}>
        <Text cursor="pointer" onClick={toggleModal}>
          {" "}
          <Icon
            as={!someSimulationFailed ? ShieldCheck : BrakeWarning}
            fill="purple.600"
          />{" "}
          Simulation
        </Text>
        <Text
          color={!someSimulationFailed ? "gray.700" : "red.500"}
          cursor="pointer"
          onClick={toggleModal}
        >
          {getSimulationLabel()}
        </Text>
      </Stack>
    )
  }

  if (isLoading || (!error && !simulations)) {
    return (
      <CardContainer title="Impact Overview">
        <Stack as="section" {...stackProps}>
          <Skeleton h="2.784375em" w="full" />
          <Skeleton h="2.784375em" w="full" />
          <Skeleton h="2.784375em" w="full" />
        </Stack>
      </CardContainer>
    )
  }

  if (executablesLength <= 0) {
    return (
      <CardContainer title="Impact Overview">
        <Stack as="section" pt={{ base: 4, sm: 0 }} spacing={4} {...stackProps}>
          <Text>
            This proposal has no associated actions and will have no impact on
            the treasury or any associated smart contracts
          </Text>
        </Stack>
      </CardContainer>
    )
  }

  if (!isLoading && !!simulations) {
    return (
      <>
        <CardContainer
          elementTitle={
            <Flex
              align="center"
              cursor="pointer"
              justify="space-between"
              width="100%"
              onClick={toggleModal}
            >
              <Text>Impact Overview</Text>
              <Icon as={ChevronRight} fill="gray.400" h={3} w={3} />
            </Flex>
          }
        >
          <Stack
            as="section"
            pt={{ base: 4, sm: 0 }}
            spacing={4}
            {...stackProps}
          >
            {displaySimulations()}
            {displayThreatAnalysis}
          </Stack>
        </CardContainer>
        <Modal isOpen={isModalOpen} onClose={toggleModal}>
          <ModalOverlay />
          <ModalContent maxWidth={600}>
            <ImpactOverview
              metadata={metadata}
              noThreatsDetected={noThreatsDetected}
              simulations={simulations}
              someSimulationFailed={someSimulationFailed}
            />
          </ModalContent>
        </Modal>
      </>
    )
  }

  return (
    <CardContainer title="Impact Overview">
      <Stack as="section" pt={{ base: 4, sm: 0 }} spacing={4} {...stackProps}>
        <Text>Impact overview is not available for this proposal</Text>
      </Stack>
    </CardContainer>
  )
}

export default TallyProposalImpactOverviewSummary
